import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PRICING, PRICING_TO_SERVICE } from "@/data/business";
import { SectionHead, EASE } from "./motion";
import { scrollToId } from "./SmoothScroll";

const Row = ({ item, i }) => {
  const quote = item.price === "Request a quote";
  return (
    <motion.li
      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: EASE, delay: i * 0.05 }}
      className="group flex items-baseline gap-4 py-5 border-b border-white/10 hover:bg-white/[0.02] transition-colors"
    >
      <span className="font-mono text-xs text-slate-400 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
      <div className="flex-1 min-w-0">
        <p className="text-base md:text-lg font-semibold text-white">{item.name}</p>
        {item.detail && <p className="text-sm text-slate-400 mt-1">{item.detail}</p>}
      </div>
      <span className="hidden sm:block flex-1 border-b border-dashed border-white/15 translate-y-[-4px]" />
      <span className={`shrink-0 text-right font-cond font-bold uppercase ${quote ? "text-sm text-slate-300 tracking-wider" : "text-2xl md:text-3xl text-[#6fd6ff]"}`}>{item.price}</span>
    </motion.li>
  );
};

export const Pricing = ({ onPick }) => {
  const [active, setActive] = useState(PRICING[0].id);
  const cat = PRICING.find((c) => c.id === active);
  const pick = () => { onPick(PRICING_TO_SERVICE[cat.id]); scrollToId("quote"); };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative bg-[#0b0d12] border-y border-white/10 py-24 md:py-36">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        <SectionHead eyebrow="02 — The Menu" lines={["Clear pricing.", "No guesswork."]}>
          All prices in CAD. Final quotes depend on vehicle size, condition and requested work. Applicable taxes extra.
        </SectionHead>
        <div className="mt-14 md:mt-20 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4 min-w-0">
            <div className="flex lg:flex-col gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 lg:mx-0 lg:px-0 lg:sticky lg:top-28" data-testid="pricing-tabs">
              {PRICING.map((c, i) => (
                <button key={c.id} onClick={() => setActive(c.id)} data-testid={`pricing-tab-${c.id}`}
                  className={`relative shrink-0 text-left px-4 lg:px-5 py-3 lg:py-4 border transition-colors ${active === c.id ? "border-[#1bb4f2] bg-[#1bb4f2]/10 text-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
                  <span className="font-mono text-[11px] text-slate-400 mr-3 hidden lg:inline">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-cond font-bold uppercase text-base lg:text-xl tracking-wide whitespace-nowrap">{c.title}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8 min-w-0">
            <div key={cat.id}>
              <motion.h3 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="font-display font-black uppercase text-2xl sm:text-4xl md:text-5xl leading-none chrome-text break-words" data-testid="pricing-category-title">{cat.title}</motion.h3>
              {cat.note && <p className="mt-4 text-slate-300 max-w-2xl">{cat.note}</p>}
              <ul className="mt-8 border-t border-white/10" data-testid="pricing-list">
                {cat.items.map((it, i) => <Row key={it.name} item={it} i={i} />)}
              </ul>
              <button onClick={pick} data-testid="pricing-request-quote" className="group mt-10 inline-flex items-center gap-3 h-14 px-7 bg-white text-[#07080B] font-semibold hover:bg-[#1bb4f2] transition-colors">
                Request a quote for {cat.title} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
